import React, { useCallback, useMemo, useRef, useState, useEffect } from "react"
import useSWR from "swr"
import {
	Box,
	Button,
	Inline,
	Stack,
	Text,
	Icons,
	CheckboxInput,
	Input,
	Grid,
	Col,
	Tooltip,
	Spinner,
	Time,
	Component,
	Tabs,
	TabsList,
	TabItem,
	TabContent,
	HorizontalScrollShadow,
	DateRangeInputWithPresets,
	Divider,
	Table,
	TableHeaderDataCell,
	TableDataCell,
	useMounted,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	Form,
	TextInputField,
	TextAreaInputField,
	validateFormValues,
	withServerErrors,
	SubmissionError,
} from "@sembark-travel/ui-form"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import * as Validator from "yup"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { formatDate } from "@sembark-travel/datetime-utils"

interface ApiError {
	response?: {
		data?: {
			errors?: Array<{ message?: string }> | Record<string, string[]>
		}
	}
	message?: string
}

type TColumn = {
	id: number
	name: string
	slug: string
	custom_column_name?: string | null
	description?: string | null
	selected?: boolean | "1" | "0" | 0 | 1
	group: {
		id: number
		name: string
	}
}
type TPreset = {
	id: number
	name: string
	tenant_id?: number | null
	description?: string | null
	created_at: string
	created_by: {
		id: number
		uid: string
		name: string
		email: string
	}
}

interface ReportTypeHeadingsResponse {
	id?: number
	headings: Array<TColumn>
	presets?: Array<TPreset>
}

interface PresetResponse {
	id: number
	name: string
	description: string
	tenant_id?: number | null
	headings: Array<TColumn>
}

function ReportTypeHeadingsXHR(xhr: XHRInstance) {
	return {
		async getHeadings(reportType: string): Promise<ReportTypeHeadingsResponse> {
			return xhr
				.get(`/report-type-headings?report_type=${reportType}`)
				.then((response) => response.data.data)
		},
		async getPreset(presetId: number): Promise<PresetResponse> {
			return xhr
				.get(`/report-type-headings/${presetId}`)
				.then((response) => response.data.data)
		},
	}
}

export interface ColumnExportProps {
	title?: string
	reportType: string
	downloadUrl?: string
	modalType: "customColumn" | "presets"
	setModalType?: (modalType: "customColumn" | "presets") => void
	allowDatesChange?: boolean
	startDate?: Date
	endDate?: Date
	/**
	 * Callback when download is requested with selected columns and date range.
	 * If provided, this will be called instead of directly downloading.
	 */
	onDownload: (data: {
		headings: Array<{
			slug: string
			custom_column_name?: string | null
		}>
		dateRange?: { from: Date; to: Date }
	}) => void
	isGenerating: boolean
}

function useReportTypeHeadings(reportType: string) {
	const xhr = useXHR()
	const mounted = useMounted()
	const {
		data,
		error,
		mutate: revalidate,
		isValidating,
		isLoading,
	} = useSWR(
		reportType ? `/report-type-headings/${reportType}` : null,
		() =>
			reportType
				? ReportTypeHeadingsXHR(xhr).getHeadings(reportType)
				: Promise.reject("Report type is required"),
		{
			onError: (error) => {
				if (mounted()) {
					showSnackbar(error?.message || "Failed to fetch report type headings")
				}
			},
			onSuccess: (data) => {
				if (!data.headings || data.headings.length === 0) {
					showSnackbar("No columns found for this report type.")
				}
			},
		}
	)

	return {
		data,
		error,
		revalidate,
		isLoading,
		isValidating,
	}
}

function usePreset(presetId: number | null) {
	const xhr = useXHR()
	const mounted = useMounted()
	const {
		data,
		error,
		mutate: revalidate,
		isValidating,
		isLoading,
	} = useSWR(
		presetId ? `/report-type-headings/preset/${presetId}` : null,
		() =>
			presetId
				? ReportTypeHeadingsXHR(xhr).getPreset(presetId)
				: Promise.reject("Preset ID is required"),
		{
			onError: (error) => {
				if (mounted()) {
					showSnackbar(error?.message || "Failed to fetch preset data")
				}
			},
		}
	)

	return {
		data,
		error,
		revalidate,
		isLoading,
		isValidating,
	}
}

export function ColumnExport({
	reportType,
	modalType,
	setModalType,
	allowDatesChange = false,
	startDate,
	endDate,
	onDownload,
	isGenerating,
}: ColumnExportProps) {
	// Ref to track the index of the column being dragged during reordering
	const dragIndexRef = useRef<number | null>(null)
	const xhr = useXHR()
	const mounted = useMounted()
	// State to track where the dragged item will be dropped (for visual indicator)
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
	const [originalColumns, setOriginalColumns] = useState<Array<TColumn>>([])
	const [filteredColumns, setFilteredColumns] = useState<Array<TColumn>>([])
	const [presets, setPresets] = useState<Array<TPreset>>([])
	const [selected, setSelected] = useState<Array<TColumn>>([])
	const [activePresetId, setActivePresetId] = useState<number | null>(null)
	const [isEditingPreset, setIsEditingPreset] = useState(false)
	const [presetUpdating, setPresetUpdating] = useState(false)

	// Date range state for allowDateChange feature
	const [dateRange, setDateRange] = useState<{
		isDateRangeDialogOpen: boolean
		tempDateRange: { from?: Date; to?: Date }
	}>(() => {
		// Initialize from explicit startDate and endDate props
		if (startDate && endDate) {
			const from = startDate
			const to = endDate
			return {
				isDateRangeDialogOpen: false,
				tempDateRange: { from, to },
			}
		}
		return {
			isDateRangeDialogOpen: false,
			tempDateRange: { from: undefined, to: undefined },
		}
	})

	const closeDateRangeDialog = () => {
		// Reset to current startDate and endDate values when closing without saving
		const currentFrom = startDate || undefined
		const currentTo = endDate || undefined
		setDateRange({
			isDateRangeDialogOpen: false,
			tempDateRange: { from: currentFrom, to: currentTo },
		})
	}

	// Sync date range when startDate and endDate change (only if allowDateChange is enabled)
	useEffect(() => {
		if (allowDatesChange && startDate && endDate) {
			const from = startDate
			const to = endDate
			// Only update if the dates are different to avoid unnecessary re-renders
			setDateRange((prev) => {
				const prevFrom = prev.tempDateRange.from
				const prevTo = prev.tempDateRange.to
				if (
					prevFrom?.getTime() !== from.getTime() ||
					prevTo?.getTime() !== to.getTime()
				) {
					return {
						...prev,
						tempDateRange: { from, to },
					}
				}
				return prev
			})
		}
	}, [allowDatesChange, startDate, endDate])

	// Stores baseline preset columns and custom names to detect unsaved changes
	const [activePresetBaseline, setActivePresetBaseline] = useState<
		Array<{
			id: number
			custom_column_name?: string | null
		}>
	>([])
	const [reportTypeId, setReportTypeId] = useState<number | null>(null)
	// Download states
	const [isSaveDialogOpen, openSaveDialog, closeSaveDialog] = useDialog()

	const isAllSelected = useMemo(() => {
		if (filteredColumns.length === 0) return false
		const selectedIds = new Set(selected.map((col) => col.id))
		return filteredColumns.every((col) => selectedIds.has(col.id))
	}, [filteredColumns, selected])

	const hasPresetChanges = useMemo(() => {
		if (!activePresetId) return false
		const current = selected.map((c) => ({
			id: c.id,
			custom_column_name: c.custom_column_name ?? null,
		}))
		// Check if number of columns changed
		if (current.length !== activePresetBaseline.length) return true
		// Check if column order or custom names changed
		for (let i = 0; i < current.length; i++) {
			if (current[i].id !== activePresetBaseline[i].id) {
				return true
			}

			if (
				current[i].custom_column_name !==
				activePresetBaseline[i].custom_column_name
			) {
				return true
			}
		}
		return false
	}, [activePresetId, selected, activePresetBaseline])
	const selectedIds = useMemo(
		() => new Set(selected.map((s) => s.id)),
		[selected]
	)

	// Helper function to organize columns by groups
	const organizeColumnsByGroups = useMemo(() => {
		const tempGroupedColumns: { [key: string]: TColumn[] } = {}
		const othersGroupColumns: TColumn[] = []
		const isSearchResult = filteredColumns.length !== originalColumns.length

		// First pass: group all columns by their group (id-name combination)
		filteredColumns.forEach((col) => {
			const groupKey = `${col.group.id}-${col.group.name}`
			if (!tempGroupedColumns[groupKey]) {
				tempGroupedColumns[groupKey] = []
			}
			tempGroupedColumns[groupKey].push(col)
		})

		// Second pass: identify single-column groups and merge them into "Others"
		const finalGroupedColumns: {
			[key: string]: { name: string; columns: TColumn[] }
		} = {}

		Object.entries(tempGroupedColumns).forEach(([groupKey, columns]) => {
			// Check if this is a search result (filteredColumns is different from originalColumns)
			if (columns.length === 1 && !isSearchResult) {
				// Single column group - add to "Others" only when not searching
				othersGroupColumns.push(...columns)
			} else {
				// Multi-column group or single column during search - keep separate
				const groupName = columns[0].group.name
				finalGroupedColumns[groupKey] = { name: groupName, columns }
			}
		})

		// Add "Others" group if there are single-column groups (only when not searching)
		if (othersGroupColumns.length > 0 && !isSearchResult) {
			finalGroupedColumns["Others"] = {
				name: "Others",
				columns: othersGroupColumns,
			}
		}

		return finalGroupedColumns
	}, [filteredColumns, originalColumns.length])

	//-------SWR hook to fetch columns---------//
	const {
		data: reportHeadingsData,
		revalidate: revalidateReportHeadings,
		isLoading: isLoadingColumns,
	} = useReportTypeHeadings(reportType)

	//-------SWR hook to fetch preset data---------//
	const {
		data: presetData,
		error: presetError,
		isLoading: isLoadingPresets,
		revalidate: revalidatePreset,
	} = usePreset(activePresetId)

	// Update state when data changes
	useEffect(() => {
		if (!mounted() || !reportHeadingsData) return

		const { headings, presets, id } = reportHeadingsData

		if (headings.length > 0) {
			const columns = (headings as Array<TColumn>).map((col: TColumn) => ({
				id: Number(col.id),
				name: col.name,
				slug: col.slug,
				description: col.description ?? null,
				group: col.group,
				selected: col.selected ?? false,
			}))
			setOriginalColumns(columns)
			setFilteredColumns(columns)

			// Set initial selected columns based on the selected property from API
			const initiallySelectedColumns = columns.filter((col) => col.selected)
			if (initiallySelectedColumns.length > 0 && modalType === "customColumn") {
				setSelected(initiallySelectedColumns)
			}
		} else {
			setOriginalColumns([])
			setFilteredColumns([])
		}

		if (presets) {
			setPresets(presets as Array<TPreset>)
		}

		if (id) {
			setReportTypeId(Number(id))
		}
	}, [reportHeadingsData, modalType, mounted])

	// Update selection when preset data changes
	useEffect(() => {
		if (!mounted() || !presetData || !originalColumns.length) return

		const { headings } = presetData
		if (Array.isArray(headings) && headings.length > 0) {
			// Map headings to actual column objects and filter out any missing ones
			const matchingColumns = headings
				.map((h) => {
					const base = originalColumns.find((c) => c.id === Number(h.id))
					return base
						? { ...base, custom_column_name: h.custom_column_name }
						: null
				})
				.filter(Boolean) as Array<TColumn>
			setSelected(matchingColumns)
			setActivePresetBaseline(
				matchingColumns.map((c) => ({
					id: c.id,
					custom_column_name: c.custom_column_name ?? null,
				}))
			)
		} else {
			// If no columns found, reset selection
			setSelected([])
			setActivePresetBaseline([])
		}
	}, [presetData, originalColumns, mounted])

	// Handle preset loading errors
	useEffect(() => {
		if (presetError && activePresetId) {
			setActivePresetId(null)
			setSelected([])
			setActivePresetBaseline([])
		}
	}, [presetError, activePresetId])

	// Clear selection and edit mode when switching to Custom Columns from Presets
	useEffect(() => {
		if (modalType === "customColumn" && (activePresetId || isEditingPreset)) {
			setSelected([])
			setActivePresetId(null)
			setActivePresetBaseline([])
			setIsEditingPreset(false)
		}
	}, [modalType, activePresetId, isEditingPreset])

	// Handle tab switching with confirmation when needed
	const handleTabSwitch = useCallback(
		async (
			targetMode: "presets" | "customColumn",
			confirm: () => Promise<boolean>
		) => {
			if (
				targetMode === "presets" &&
				modalType === "customColumn" &&
				selected.length > 0
			) {
				const confirmed = await confirm()

				if (!confirmed) {
					return
				}

				setSelected([])
				setActivePresetId(null)
				setActivePresetBaseline([])
				setIsEditingPreset(false)
			}

			setModalType?.(targetMode)
		},
		[modalType, selected.length, setModalType]
	)

	const toggleSelection = useCallback((col: TColumn, checked: boolean) => {
		setSelected((prev) => {
			if (checked) {
				if (prev.find((p) => p.id === col.id)) return prev
				return prev.concat(col)
			}
			return prev.filter((p) => p.id !== col.id)
		})
	}, [])

	const onToggleAll = useCallback(
		(checked: boolean) => {
			setSelected((prev) => {
				if (checked) {
					// Add all columns that are not in the selected
					const prevIds = new Set(prev.map((col) => col.id))
					const newColumns = filteredColumns.filter(
						(col) => !prevIds.has(col.id)
					)
					return [...prev, ...newColumns]
				} else {
					// Remove columns that are not in the filteredColumns
					return prev.filter(
						(col) => !filteredColumns.find((c) => c.id === col.id)
					)
				}
			})
		},
		[filteredColumns]
	)

	// Helper function to toggle all columns in a group
	const toggleGroupSelection = useCallback(
		(groupColumns: TColumn[], checked: boolean) => {
			setSelected((prev) => {
				if (checked) {
					// Add all group columns that aren't already selected
					const newColumns = groupColumns.filter(
						(col) => !prev.find((p) => p.id === col.id)
					)
					return prev.concat(newColumns)
				} else {
					// Remove all group columns
					return prev.filter((p) => !groupColumns.find((gc) => gc.id === p.id))
				}
			})
		},
		[]
	)

	const onDragStart = (index: number) => () => {
		dragIndexRef.current = index
		setDragOverIndex(null) // Clear any previous drag over indicator
	}

	// Handle drag over to show visual indicator
	const onDragOverWithIndex = (index: number) => (e: React.DragEvent) => {
		e.preventDefault()
		if (dragIndexRef.current !== null && dragIndexRef.current !== index) {
			setDragOverIndex(index)
		}
	}

	// Handle drag leave to clear visual indicator
	const onDragLeave = () => {
		setDragOverIndex(null)
	}

	// Moves the dragged column to the drop position and updates indices
	const onDrop = (dropIndex: number) => (e: React.DragEvent) => {
		e.preventDefault()
		const from = dragIndexRef.current
		if (from === null || from === dropIndex) return
		setSelected((prev) => {
			const next = prev.slice()
			const [moved] = next.splice(from, 1)
			next.splice(dropIndex, 0, moved)
			return next
		})
		dragIndexRef.current = null
		setDragOverIndex(null) // Clear drag over indicator
	}

	// Select the clicked preset - SWR will automatically fetch the data
	const handlePresetClick = useCallback(
		(presetId: number) => {
			if (activePresetId === presetId) return

			setActivePresetId(presetId)
		},
		[activePresetId]
	)

	//search for a column when user types in the search input
	const handleSearch = useCallback(
		(query: string) => {
			if (!query.trim()) {
				setFilteredColumns(originalColumns)
			} else {
				setFilteredColumns(
					originalColumns.filter((col) =>
						col.name.toLowerCase().includes(query.toLowerCase())
					)
				)
			}
			dragIndexRef.current = null
		},
		[originalColumns]
	)

	//fetch create preset api when save button is clicked
	const createPreset = useCallback(
		async (name: string, description: string) => {
			if (!reportTypeId) {
				showSnackbar("Report Type not loaded. Please try again.")
				return
			}
			const payload = {
				name,
				description,
				report_type_id: reportTypeId,
				headings: selected.map((s) => ({
					id: s.id,
					custom_column_name: s.custom_column_name,
				})),
			}
			try {
				await xhr.post("/report-type-headings", payload)
				showSnackbar("Preset created successfully.")
				setSelected([])
				setActivePresetId(null)
				closeSaveDialog()
				setModalType?.("presets")
				/* Revalidate headings data to refresh presets list */
				await revalidateReportHeadings()
			} catch (error: unknown) {
				const apiError = error as ApiError
				if (
					apiError?.response?.data?.errors &&
					typeof apiError.response.data.errors === "object"
				) {
					Object.entries(apiError.response.data.errors).forEach(
						([field, messages]) => {
							if (Array.isArray(messages)) {
								messages.forEach((msg) =>
									showSnackbar(msg || "An error occurred.")
								)
							}
						}
					)
				} else if (Array.isArray(apiError?.response?.data?.errors)) {
					apiError.response.data.errors.forEach((e: { message?: string }) => {
						showSnackbar(e?.message || "An error occurred.")
					})
				} else {
					showSnackbar(
						apiError?.message || "Failed to create preset. Please try again."
					)
				}
			}
		},
		[
			reportTypeId,
			selected,
			xhr,
			closeSaveDialog,
			setModalType,
			revalidateReportHeadings,
		]
	)

	//fetch update preset api when update is clicked for a preset
	const handleUpdatePreset = useCallback(async () => {
		if (!activePresetId) return
		setPresetUpdating(true)
		try {
			if (presetUpdating) return
			await xhr.put(`/report-type-headings/${activePresetId}`, {
				report_type_id: reportTypeId,
				headings: selected.map((s) => ({
					id: s.id,
					custom_column_name: s.custom_column_name,
				})),
			})
			showSnackbar("Preset updated successfully.")
			setActivePresetBaseline(
				selected.map((s) => ({
					id: s.id,
					custom_column_name: s.custom_column_name ?? null,
				}))
			)

			await revalidatePreset()
		} catch (error) {
			showSnackbar("Failed to update preset. Please try again.")
		} finally {
			setPresetUpdating(false)
		}
	}, [
		activePresetId,
		selected,
		xhr,
		reportTypeId,
		presetUpdating,
		revalidatePreset,
	])

	//fetch delete preset api when delete is clicked for a preset
	const handleDeletePreset = useCallback(
		async (presetId: number) => {
			try {
				await xhr.delete(`/report-type-headings/${presetId}`)
				// Remove preset from local state
				setPresets((prev) => prev.filter((p) => p.id !== presetId))

				//Reset selection and active preset id
				setActivePresetId(null)
				setSelected([])
				setActivePresetBaseline([])
				setIsEditingPreset(false)
				showSnackbar("Preset deleted successfully.")
			} catch (error: unknown) {
				const e = error as Error
				showSnackbar(e.message || "Failed to delete preset. Please try again.")
			}
		},
		[xhr]
	)

	//start the download process when download button is clicked
	const handleDownload = useCallback(async () => {
		if (selected.length === 0 || isGenerating) return

		// If onDownload callback is provided
		if (onDownload) {
			onDownload({
				headings: selected.map((s) => ({
					slug: s.slug,
					custom_column_name: s.custom_column_name,
				})),
				dateRange:
					dateRange.tempDateRange.from && dateRange.tempDateRange.to
						? {
								from: dateRange.tempDateRange.from,
								to: dateRange.tempDateRange.to,
							}
						: undefined,
			})
			return
		}
	}, [selected, isGenerating, onDownload, dateRange.tempDateRange])

	return (
		<Stack
			paddingTop={
				dateRange.tempDateRange.from && dateRange.tempDateRange.to ? "3" : "0"
			}
		>
			{dateRange.tempDateRange.from && dateRange.tempDateRange.to ? (
				<Box
					position="relative"
					padding="3"
					paddingTop="6"
					borderRadius="md"
					borderWidth="1"
					borderColor="muted"
				>
					<Inline
						position="absolute"
						borderRadius="lg"
						borderWidth="1"
						borderColor="muted"
						backgroundColor="primary"
						paddingX="2"
						paddingY="1"
						style={{ top: "-15px" }}
					>
						<Text fontWeight="semibold">
							Date{" "}
							{dateRange.tempDateRange.from && dateRange.tempDateRange.to
								? "Range"
								: "Selected"}
						</Text>
					</Inline>
					<Inline
						alignItems="center"
						justifyContent="between"
						gap="2"
						flexWrap="wrap"
						width="full"
					>
						<Inline alignItems="center" gap="2">
							<Box
								borderRadius="md"
								borderWidth="1"
								borderColor="default"
								padding="2"
								backgroundColor="inset"
							>
								{dateRange.tempDateRange.from
									? formatDate(dateRange.tempDateRange.from, "DD MMM, YYYY")
									: ""}
							</Box>
							<Box>
								<Icons.ArrowLeft rotate="180" />
							</Box>
							<Box
								borderRadius="md"
								borderWidth="1"
								borderColor="default"
								padding="2"
								backgroundColor="inset"
							>
								{dateRange.tempDateRange.to
									? formatDate(dateRange.tempDateRange.to, "DD MMM, YYYY")
									: ""}
							</Box>
						</Inline>
						{allowDatesChange && (
							<Button
								size="sm"
								status="primary"
								onClick={() => {
									setDateRange({
										...dateRange,
										isDateRangeDialogOpen: true,
									})
								}}
							>
								<Icons.Pencil />
							</Button>
						)}
					</Inline>

					<DateRangeDialog
						open={dateRange.isDateRangeDialogOpen}
						initialFrom={dateRange.tempDateRange.from}
						initialTo={dateRange.tempDateRange.to}
						onClose={closeDateRangeDialog}
						onSave={(from, to) => {
							setDateRange({
								isDateRangeDialogOpen: false,
								tempDateRange: { from, to },
							})
						}}
					/>
				</Box>
			) : null}
			<Tabs layout="col" gap="2">
				<Stack gap="3">
					<ConfirmationDialogProvider>
						{({ confirm }) => (
							<>
								<TabsList>
									<TabItem
										active={modalType === "presets"}
										onClick={() => handleTabSwitch("presets", confirm)}
										disabled={!setModalType}
									>
										<Icons.HorizontalSliders /> Presets
									</TabItem>
									<TabItem
										active={modalType === "customColumn"}
										onClick={() => handleTabSwitch("customColumn", confirm)}
										disabled={!setModalType}
									>
										<Icons.Columns /> Custom Columns
									</TabItem>
								</TabsList>

								<ConfirmationDialog
									title="Switching to Presets?"
									destructiveAction
								>
									<Stack gap="4">
										<Stack gap="2">
											<Text color="warning" fontWeight="semibold">
												<Icons.Attention /> You have {selected.length} column
												{selected.length !== 1 ? "s" : ""} selected
											</Text>
											<Text fontSize="sm" color="muted">
												Switching to Presets will clear your current custom
												column selection.
											</Text>
										</Stack>
										<Text>Are you sure you want to switch to Presets?</Text>
									</Stack>
								</ConfirmationDialog>
							</>
						)}
					</ConfirmationDialogProvider>

					<Inline justifyContent="end">
						<Button
							level="primary"
							size="sm"
							onClick={handleDownload}
							disabled={!selected.length || isGenerating}
							aria-busy={isGenerating}
						>
							<Icons.DocumentDownload
								animation={isGenerating ? "pulse" : undefined}
							/>
							{isGenerating ? "Downloading..." : "Download Report"}
						</Button>
					</Inline>
				</Stack>

				<TabContent>
					<Stack gap="8">
						{modalType === "presets" ? (
							<PresetsSection
								presets={presets}
								isLoadingColumns={isLoadingColumns}
								activePresetId={activePresetId}
								handlePresetClick={handlePresetClick}
								isEditingPreset={isEditingPreset}
								setIsEditingPreset={setIsEditingPreset}
								handleDeletePreset={handleDeletePreset}
							/>
						) : null}
						{modalType === "customColumn" || isEditingPreset ? (
							<ColumnsSection
								isAllSelected={isAllSelected}
								onToggleAll={onToggleAll}
								handleSearch={handleSearch}
								organizeColumnsByGroups={organizeColumnsByGroups}
								selectedIds={selectedIds}
								toggleGroupSelection={toggleGroupSelection}
								toggleSelection={toggleSelection}
								isLoadingColumns={isLoadingColumns}
							/>
						) : null}
						<Stack>
							<Box
								position="relative"
								padding="3"
								paddingTop="6"
								borderRadius="md"
								borderWidth="1"
								borderColor="muted"
								backgroundColor="subtle"
							>
								<Inline
									position="absolute"
									borderRadius="lg"
									borderWidth="1"
									borderColor="muted"
									backgroundColor="primary"
									paddingX="2"
									paddingY="1"
									style={{
										top: "-15px",
									}}
								>
									<Text fontWeight="semibold">
										{`Selected Columns (${selected?.length})`}
									</Text>
								</Inline>
								{(modalType !== "presets" || isEditingPreset) &&
								selected.length > 0 ? (
									<Inline
										position="absolute"
										style={{
											top: "-15px",
											right: "15px",
										}}
									>
										<EditColumnNamesDialog
											selected={selected}
											presetName={
												presets.find((p) => p.id === activePresetId)?.name || ""
											}
											setSelected={setSelected}
										>
											{({ show }) => (
												<Button size="sm" level="secondary" onClick={show}>
													<Icons.Pencil />
													<Text>Edit Column Names</Text>
												</Button>
											)}
										</EditColumnNamesDialog>
									</Inline>
								) : null}
								{isLoadingPresets ? (
									<Stack alignItems="center" paddingY="4">
										<Spinner />
									</Stack>
								) : selected.length === 0 ? (
									<Stack alignItems="center" paddingBottom="2">
										<Icons.Info size="6" color="muted" />
										<Text color="muted" fontWeight="semibold">
											No Selected Columns
										</Text>
									</Stack>
								) : (
									<Box
										style={{
											maxHeight: "300px",
											overflowY: "auto",
										}}
									>
										<Inline gap="3" flexWrap="wrap">
											{selected.map((s, index) => {
												const isReorderDisabled =
													modalType === "presets" && !isEditingPreset
												const dragIndex = dragIndexRef.current as number
												const showDropIndicator =
													dragOverIndex === index &&
													dragIndex !== null &&
													dragIndex !== index

												return (
													<React.Fragment key={s.id}>
														{showDropIndicator && dragIndex > index && (
															<Box
																height="full"
																borderRadius="none"
																marginY="auto"
																style={{
																	width: "2px",
																	minHeight: "40px",
																	backgroundColor: "#3e63dd",
																}}
															/>
														)}

														<Box
															draggable={!isReorderDisabled}
															onDragStart={
																isReorderDisabled
																	? undefined
																	: onDragStart(index)
															}
															onDragOver={
																isReorderDisabled
																	? undefined
																	: onDragOverWithIndex(index)
															}
															onDragLeave={
																isReorderDisabled ? undefined : onDragLeave
															}
															onDrop={
																isReorderDisabled ? undefined : onDrop(index)
															}
															paddingX="1"
															paddingY="1"
															paddingRight={isReorderDisabled ? "2" : "1"}
															borderWidth="1"
															rounded="md"
															backgroundColor="default"
															style={{
																cursor: isReorderDisabled ? "default" : "grab",
															}}
														>
															<Inline gap="8" alignItems="center">
																<Inline gap="2" alignItems="center">
																	<Box
																		paddingX="2"
																		paddingY="1"
																		bgColor="inset"
																		rounded="md"
																	>
																		{index + 1}
																	</Box>

																	<Text>
																		{s.custom_column_name ? (
																			<Inline gap="1">
																				<Text>{s.custom_column_name}</Text>
																				<Text color="muted">({s.name})</Text>
																			</Inline>
																		) : (
																			s.name
																		)}
																	</Text>
																</Inline>

																{!isReorderDisabled && (
																	<Inline
																		alignItems="center"
																		bgColor="primary"
																		rounded="md"
																		paddingX="2"
																		paddingY="1"
																		style={{
																			height: 26,
																			width: 26,
																		}}
																	>
																		<Icons.ArrowRightLeft
																			size="3"
																			color="primary"
																		/>
																	</Inline>
																)}
															</Inline>
														</Box>
														{showDropIndicator && dragIndex < index && (
															<Box
																height="full"
																borderRadius="none"
																marginY="auto"
																style={{
																	width: "2px",
																	minHeight: "40px",
																	backgroundColor: "#3e63dd",
																}}
															/>
														)}
													</React.Fragment>
												)
											})}
										</Inline>
									</Box>
								)}
							</Box>
							{selected?.length > 0 &&
								(modalType === "customColumn" ||
									(modalType === "presets" && isEditingPreset)) && (
									<Text color="muted" fontSize="sm">
										Note: Drag and drop to re-arrange columns
									</Text>
								)}
						</Stack>
						<Inline gap="3" justifyContent="end" flexWrap="wrap">
							{(modalType === "customColumn" || isEditingPreset) && (
								<Button
									onClick={openSaveDialog}
									status="primary"
									disabled={!selected.length}
								>
									<Icons.Ok />
									{activePresetId && isEditingPreset ? "Save as New" : "Save"}
								</Button>
							)}
							<SavePresetDialog
								open={isSaveDialogOpen}
								onClose={closeSaveDialog}
								selectedCount={selected.length}
								onSave={createPreset}
							/>
							{/* Update button - only shown when preset has changes in column selection, order, or custom names */}
							{activePresetId && isEditingPreset && hasPresetChanges && (
								<Button
									status="primary"
									onClick={handleUpdatePreset}
									disabled={selected?.length === 0 || presetUpdating}
								>
									<Icons.Ok animation={presetUpdating ? "pulse" : undefined} />
									{presetUpdating ? "Updating..." : "Update"}
								</Button>
							)}
						</Inline>
					</Stack>
				</TabContent>
			</Tabs>
		</Stack>
	)
}

type PresetSectionProps = {
	presets: Array<TPreset>
	isLoadingColumns: boolean
	activePresetId: number | null
	handlePresetClick: (presetId: number) => void
	isEditingPreset: boolean
	setIsEditingPreset: (isEditingPreset: boolean) => void
	handleDeletePreset: (presetId: number) => void
}

function PresetsSection({
	presets,
	isLoadingColumns,
	activePresetId,
	handlePresetClick,
	isEditingPreset,
	setIsEditingPreset,
	handleDeletePreset,
}: PresetSectionProps) {
	return (
		<Box
			position="relative"
			padding="3"
			paddingTop="6"
			borderRadius="md"
			borderWidth="1"
			borderColor="muted"
		>
			<Inline
				position="absolute"
				borderRadius="lg"
				borderWidth="1"
				borderColor="muted"
				backgroundColor="primary"
				paddingX="2"
				paddingY="1"
				style={{ top: "-15px" }}
			>
				<Text fontWeight="semibold">{`Saved Presets (${presets.length})`}</Text>
			</Inline>

			{isLoadingColumns ? (
				<Stack alignItems="center" paddingY="4">
					<Spinner />
				</Stack>
			) : presets.length === 0 ? (
				<Stack alignItems="center" paddingBottom="2">
					<Icons.Info size="6" color="muted" />
					<Text color="muted" fontWeight="semibold">
						No presets available
					</Text>
				</Stack>
			) : (
				<HorizontalScrollShadow>
					<Inline gap="4" flexWrap="nowrap">
						{presets.map((p) => {
							const isActive = activePresetId === p.id
							return (
								<Box
									key={p.id}
									paddingY="2"
									paddingX={isActive ? "2" : "3"}
									rounded="md"
									borderWidth="1"
									minWidth="max"
									role="button"
									cursor="pointer"
									onClick={() => {
										handlePresetClick(p.id)
										if (activePresetId !== p.id) {
											setIsEditingPreset(false)
										}
									}}
									{...(isActive
										? { borderColor: "primary", backgroundColor: "primary" }
										: {
												borderColor: "default",
												backgroundColor: "default",
											})}
								>
									<Inline alignItems="center" gap="6">
										<Stack style={{ flex: 1 }} gap="1">
											{(() => {
												const nameNeedsTruncation = p.name.length > 50
												const descriptionNeedsTruncation =
													p.description && p.description.length > 50

												return (
													<Stack>
														{nameNeedsTruncation ? (
															<Tooltip content={p.name} placement="top-start">
																<Text
																	fontWeight="semibold"
																	textOverflow="truncate"
																	style={{
																		maxWidth: "200px",
																	}}
																>
																	{`${p.name.substring(0, 50)}...`}
																</Text>
															</Tooltip>
														) : (
															<Text fontWeight="semibold">{p.name}</Text>
														)}
														{descriptionNeedsTruncation ? (
															<Tooltip
																content={p.description || ""}
																placement="top-start"
															>
																<Text
																	fontSize="xs"
																	color="muted"
																	textOverflow="truncate"
																	style={{ maxWidth: "200px" }}
																>
																	{`${(p.description || "").substring(0, 50)}...`}
																</Text>
															</Tooltip>
														) : (
															<Text fontSize="xs" color="muted">
																{p.description}
															</Text>
														)}
													</Stack>
												)
											})()}
											<Text fontSize="xs" color="muted">
												Created on <Time timestamp={p.created_at} />
												<br />
												{p.created_by?.name ? `by ${p.created_by?.name}` : null}
											</Text>
										</Stack>
										{isActive && p.tenant_id !== null ? (
											<Inline gap="px">
												<Button
													level="tertiary"
													size="sm"
													onClick={() => {
														setIsEditingPreset(!isEditingPreset)
														handlePresetClick(p.id)
													}}
												>
													<Icons.Pencil size="4" />
												</Button>
												<ConfirmationDialogProvider>
													{({ confirm }) => (
														<>
															<Component initialState={false}>
																{({ state, setState }) => (
																	<Button
																		status="danger"
																		level="tertiary"
																		size="sm"
																		disabled={state}
																		onClick={() => {
																			confirm().then(async (confirmed) => {
																				if (confirmed) {
																					setState(true)
																					try {
																						await handleDeletePreset(p.id)
																					} finally {
																						setState(false)
																					}
																				}
																			})
																		}}
																	>
																		<Icons.Trash size="4" />
																	</Button>
																)}
															</Component>
															<ConfirmationDialog
																title="Delete Preset"
																destructiveAction
															>
																<Stack gap="4">
																	<Stack gap="2">
																		<Text color="danger" fontWeight="semibold">
																			<Icons.Attention /> Once deleted, Preset
																			cannot be restored.
																		</Text>
																		<Text fontSize="sm" color="muted">
																			You will be able to add a new one.
																		</Text>
																	</Stack>
																	<Text>
																		Are you sure you want to delete "{p.name}"
																		Preset?
																	</Text>
																</Stack>
															</ConfirmationDialog>
														</>
													)}
												</ConfirmationDialogProvider>
											</Inline>
										) : null}
									</Inline>
								</Box>
							)
						})}
					</Inline>
				</HorizontalScrollShadow>
			)}
		</Box>
	)
}

type ColumnsSectionProps = {
	isAllSelected: boolean
	onToggleAll: (isAllSelected: boolean) => void
	handleSearch: (search: string) => void
	toggleSelection: (col: TColumn, isSelected: boolean) => void
	selectedIds: Set<number>
	isLoadingColumns: boolean
	toggleGroupSelection: (
		groupColumns: Array<TColumn>,
		isGroupSelected: boolean
	) => void
	organizeColumnsByGroups: Record<string, { name: string; columns: TColumn[] }>
}
function ColumnsSection({
	isAllSelected,
	onToggleAll,
	handleSearch,
	toggleGroupSelection,
	selectedIds,
	toggleSelection,
	isLoadingColumns,
	organizeColumnsByGroups,
}: ColumnsSectionProps) {
	return (
		<Box
			position="relative"
			padding="3"
			borderRadius="md"
			borderWidth="1"
			borderColor="muted"
		>
			<Inline
				position="absolute"
				borderRadius="lg"
				borderWidth="1"
				borderColor="muted"
				backgroundColor="primary"
				paddingX="2"
				paddingY="1"
				style={{ top: "-15px" }}
			>
				<Text fontWeight="semibold">Columns</Text>
			</Inline>
			<Stack gap="3">
				<Grid alignItems="center" justifyContent="end" noGutters gap="6">
					<Inline gap="2" as="label">
						<CheckboxInput
							checked={isAllSelected}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								onToggleAll(e.target.checked)
							}
						/>
						<Text>Select All</Text>
					</Inline>
					<Col xs={12} sm={6} md={4} paddingX="3">
						<Input
							type="search"
							placeholder="Search by column name..."
							onChange={(e) => handleSearch(e.currentTarget.value)}
						/>
					</Col>
				</Grid>
				<HorizontalScrollShadow>
					<Inline gap="4" alignItems="start" position="relative">
						{isLoadingColumns ? (
							<Stack alignItems="center" paddingY="4" width="full">
								<Spinner />
							</Stack>
						) : (
							Object.entries(organizeColumnsByGroups).map(
								([groupKey, groupData]) => (
									<Box
										key={groupKey}
										borderWidth="1"
										padding="4"
										rounded="md"
										overflow="auto"
										style={{
											maxHeight: "380px",
											minWidth: "480px",
										}}
									>
										<Stack gap="3">
											{/* Group header */}
											<Inline
												as="label"
												gap="2"
												alignItems="center"
												whiteSpace="nowrap"
												cursor="pointer"
											>
												<CheckboxInput
													checked={groupData.columns.every((col) =>
														selectedIds.has(col.id)
													)}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														toggleGroupSelection(
															groupData.columns,
															e.target.checked
														)
													}
												/>
												<Text fontWeight="semibold" textDecoration="underline">
													{groupData.name}
												</Text>
											</Inline>

											{/* Render all columns in this group */}
											{groupData.columns.map((col: TColumn) => {
												return (
													<Inline
														as="label"
														key={col.id}
														gap="2"
														alignItems="center"
														whiteSpace="nowrap"
														cursor="pointer"
														paddingLeft="6"
													>
														<CheckboxInput
															checked={selectedIds.has(col.id)}
															onChange={(
																e: React.ChangeEvent<HTMLInputElement>
															) => toggleSelection(col, e.target.checked)}
														/>
														<Text fontWeight="semibold">{col.name}</Text>
														{col.description && (
															<Text
																fontWeight="normal"
																color="muted"
															>{` (e.g: ${col.description})`}</Text>
														)}
													</Inline>
												)
											})}
										</Stack>
									</Box>
								)
							)
						)}
					</Inline>
				</HorizontalScrollShadow>
			</Stack>
		</Box>
	)
}

export default ColumnExport

export function ColumnExportInDialog({
	children,
	onClose: onCloseCallback,
	allowDatesChange = false,
	onDownload,
	startDate,
	endDate,
	...props
}: Omit<ColumnExportProps, "modalType"> & {
	children: (props: {
		open: (modalType: "customColumn" | "presets") => void
	}) => React.ReactNode
	onClose?: () => void
	allowDatesChange?: boolean
	startDate?: Date
	endDate?: Date
	onDownload?: (data: {
		headings?: Array<{ slug: string }>
		dateRange?: { from: Date; to: Date }
	}) => void
}) {
	const [isOpen, openDialog, close] = useDialog()
	const [modalType, setModalType] = useState<"customColumn" | "presets">(
		"presets"
	)

	const open = (type: "customColumn" | "presets") => {
		setModalType(type)
		openDialog()
	}

	const handleClose = () => {
		close()
		onCloseCallback?.()
	}

	return (
		<>
			{children({ open })}
			<Dialog
				open={isOpen}
				onClose={handleClose}
				title={props.title || "Export Columns"}
				lg
			>
				<Dialog.Body>
					<ColumnExport
						{...props}
						modalType={modalType}
						setModalType={setModalType}
						allowDatesChange={allowDatesChange}
						startDate={startDate}
						endDate={endDate}
						onDownload={onDownload}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type SavePresetDialogProps = {
	open: boolean
	onClose: () => void
	selectedCount: number
	onSave: (name: string, description: string) => Promise<void>
}

function SavePresetDialog({
	open,
	onClose,
	selectedCount,
	onSave,
}: SavePresetDialogProps) {
	const initialValues = { name: "", description: "" }

	const validationSchema = Validator.object().shape({
		name: Validator.string()
			.trim()
			.max(100, "Please use 100 or less characters")
			.min(3, "Please use atleast 3 characters")
			.required("Please provide the name for the preset"),
		description: Validator.string()
			.trim()
			.max(150, "Please use 150 or less characters")
			.min(3, "Please use atleast 3 characters")
			.required("Please provide the description for the preset"),
	})

	return (
		<Dialog open={open} onClose={onClose} title="Save Filter Configuration" sm>
			<Form<typeof initialValues>
				initialValues={initialValues}
				validate={validateFormValues(validationSchema)}
				onSubmit={withServerErrors(async ({ name, description }) => {
					await onSave(name, description)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Dialog.Body>
							<Stack gap="4">
								<TextInputField
									name="name"
									type="text"
									label="Name of the Preset"
									placeholder="Enter preset name"
									required
								/>
								<TextAreaInputField
									name="description"
									label="Description of the Preset"
									placeholder="Enter preset description"
									rows={3}
									required
								/>
								<Stack
									gap="1"
									backgroundColor="primary"
									padding="2"
									rounded="md"
								>
									<Text fontWeight="semibold">
										Selected Columns: {selectedCount}
									</Text>
									<Text>Order will be preserved as arranged</Text>
								</Stack>
								<SubmissionError />
							</Stack>
						</Dialog.Body>
						<Dialog.Footer>
							<Inline gap="4" justifyContent="end">
								<Button
									level="tertiary"
									onClick={onClose}
									disabled={submitting}
								>
									Cancel
								</Button>
								<Button level="primary" type="submit" disabled={submitting}>
									<Icons.Ok {...(submitting && { animation: "pulse" })} />
									{submitting ? "Saving..." : "Save"}
								</Button>
							</Inline>
						</Dialog.Footer>
					</form>
				)}
			</Form>
		</Dialog>
	)
}

export function DateRangeDialog({
	title = "Date Range",
	open,
	initialFrom,
	initialTo,
	onClose: onCloseCallback,
	onSave,
	saveButtonText = "Done",
	disableSaveButton = false,
}: {
	title?: string
	open: boolean
	initialFrom?: Date
	initialTo?: Date
	onClose: () => void
	onSave: (from: Date, to: Date) => void
	saveButtonText?: string
	disableSaveButton?: boolean
}) {
	const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({
		from: initialFrom,
		to: initialTo,
	})

	const onChange = (range: { from?: Date; to?: Date }) => {
		setDateRange(range)
	}

	const onClose = () => {
		setDateRange({ from: initialFrom, to: initialTo })
		onCloseCallback?.()
	}

	return (
		<Dialog open={open} onClose={onClose} md>
			<Dialog.Header>
				<Dialog.Title>{title}</Dialog.Title>
			</Dialog.Header>
			<DateRangeInputWithPresets
				from={dateRange.from}
				to={dateRange.to}
				onChange={onChange}
				dateFormat="DD MMM, YYYY"
			/>
			<Dialog.Footer>
				<Inline gap="4" justifyContent="end">
					<Button level="tertiary" onClick={onClose}>
						Cancel
					</Button>
					<Button
						level="primary"
						onClick={() => {
							if (dateRange.from && dateRange.to) {
								onSave(dateRange.from, dateRange.to)
							}
						}}
						disabled={!dateRange.from || !dateRange.to || disableSaveButton}
					>
						{saveButtonText}
					</Button>
				</Inline>
			</Dialog.Footer>
		</Dialog>
	)
}

function EditColumnNamesDialog({
	selected: columns,
	presetName,
	setSelected,
	children,
}: {
	selected: TColumn[]
	presetName: string
	setSelected: React.Dispatch<React.SetStateAction<TColumn[]>>
	children: (props: { show: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({
				show: open,
			})}
			<Dialog
				open={isOpen}
				onClose={close}
				title={`Edit Column Names (${presetName})`}
			>
				<Dialog.Body>
					<EditColumnNamesForm
						columns={columns}
						onSubmit={(updatedColumns) => {
							setSelected(updatedColumns)
							close()
						}}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function EditColumnNamesForm({
	columns,
	onSubmit,
	onCancel,
}: {
	columns: TColumn[]
	onSubmit: (updatedColumns: TColumn[]) => void
	onCancel: () => void
}) {
	type FormValues = {
		columns: TColumn[]
	}

	const initialValues: FormValues = {
		columns,
	}

	const validationSchema = Validator.object({
		columns: Validator.array().of(
			Validator.object({
				custom_column_name: Validator.string()
					.trim()
					.max(50, "Maximum 50 characters")
					.nullable(),
			})
		),
	})

	return (
		<Form<FormValues>
			initialValues={initialValues}
			validate={validateFormValues(validationSchema)}
			onSubmit={({ columns }) => onSubmit(columns)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => {
				return (
					<form noValidate onSubmit={handleSubmit}>
						<Stack borderWidth="1" rounded="md" overflow="hidden">
							<Table
								bordered
								responsive
								children={
									<>
										<Box as="thead" backgroundColor={"inset"}>
											<tr>
												<TableHeaderDataCell>System Names</TableHeaderDataCell>
												<TableHeaderDataCell>Custom Name</TableHeaderDataCell>
											</tr>
										</Box>
										<tbody>
											{columns.map((column, index) => (
												<Box as="tr" key={column.id}>
													<TableDataCell>
														<Text>{column.name}</Text>
													</TableDataCell>

													<TableDataCell>
														<TextInputField
															name={`columns.${index}.custom_column_name`}
															placeholder={`e.g. ${column.name}`}
														/>
													</TableDataCell>
												</Box>
											))}
										</tbody>
									</>
								}
							/>
						</Stack>

						<SubmissionError />
						<Divider sm />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Please wait..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</form>
				)
			}}
		</Form>
	)
}
